import { useEffect, useState } from 'react'

import {
  CreateAudiostream,
  CreateAudiostreamInput,
  CreateAudiostreamVariables,
  deleteAudiostream,
  deleteAudiostreamVariables,
  GetStation,
  GetStationAudiostreams,
  GetStationAudiostreamsVariables,
  GetStationVariables,
  GetStreamSchedulesForSelection,
  GetStreamSchedulesForSelectionVariables,
  UpdateAudiostream,
  UpdateAudiostreamInput,
  UpdateAudiostreamVariables,
} from 'types/graphql'

import { Metadata, useMutation, useQuery } from '@cedarjs/web'
import { toast } from '@cedarjs/web/toast'

import { CommonArrayItem } from 'src/common/types'
import Card from 'src/components/Custom/Card/Card'
import ErrorMessage from 'src/components/Custom/ErrorMessage/ErrorMessage'
import HeadingLevel from 'src/components/Custom/Heading/Heading'
import Loader from 'src/components/Custom/Loader/Loader'
import useUserStation from 'src/lib/hooks/useUserStation'

import { GET_STATION, GET_STREAM_SCHEDULES } from '../queries'

import Audiostream, { Audiostreams } from './Audiostream.tsx'
import {
  CREATE_AUDIOSTREAM,
  DELETE_AUDIOSTREAM,
  GET_STATION_AUDIOSTREAMS,
  UPDATE_AUDIOSTREAM,
} from './queries'

type AudiostreamManagerPageState = {
  audioData: Audiostreams
}

type AudiostreamManagerPageProps = {
  id?: string
}

const AudiostreamManagerPage = ({ id }: AudiostreamManagerPageProps) => {
  const [audiostreamState, setAudiostreamState] =
    useState<AudiostreamManagerPageState>(null)
  const [invalidStation, setInvalidStation] = useState(false)
  const [streamScheduleOptions, setStreamScheduleOptions] = useState<
    CommonArrayItem[]
  >([])
  const userStationId = useUserStation()
  const stationId = id ?? userStationId

  const { data: audiostreamData, refetch: refetchAudiostreamData } = useQuery<
    GetStationAudiostreams,
    GetStationAudiostreamsVariables
  >(GET_STATION_AUDIOSTREAMS, {
    variables: { stationId },
  })
  useQuery<
    GetStreamSchedulesForSelection,
    GetStreamSchedulesForSelectionVariables
  >(GET_STREAM_SCHEDULES, {
    variables: { stationId },
    onCompleted: ({ streamSchedules }) => {
      setStreamScheduleOptions([
        { label: 'None', value: '' },
        ...streamSchedules
          .filter(
            (streamSchedule) => streamSchedule.streamMediaType === 'AUDIO'
          )
          .map((streamSchedule) => ({
            label: streamSchedule.name,
            value: streamSchedule.id,
          })),
      ])
    },
  })

  const { data: stationData, loading: loadingStation } = useQuery<
    GetStation,
    GetStationVariables
  >(GET_STATION, {
    variables: { id: stationId },
  })

  const [
    createAudiostream,
    {
      loading: loadingCreateAudiostream,
      error: createError,
      reset: resetCreateAudiostream,
    },
  ] = useMutation<CreateAudiostream, CreateAudiostreamVariables>(
    CREATE_AUDIOSTREAM,
    {
      onCompleted: () => refetchAudiostreamData(),
    }
  )

  const [
    updateAudiostream,
    {
      loading: loadingUpdateAudiostream,
      error: updateError,
      reset: resetUpdateAudiostream,
    },
  ] = useMutation<UpdateAudiostream, UpdateAudiostreamVariables>(
    UPDATE_AUDIOSTREAM
  )

  const [
    deleteAudiostream,
    {
      loading: loadingDeleteAudiostream,
      error: deleteError,
      reset: resetDeleteAudiostream,
    },
  ] = useMutation<deleteAudiostream, deleteAudiostreamVariables>(
    DELETE_AUDIOSTREAM,
    {
      onCompleted: () => refetchAudiostreamData(),
    }
  )

  const handleModalClose = () => {
    resetCreateAudiostream()
    resetUpdateAudiostream()
    resetDeleteAudiostream()
  }
  const handleAudiostreamCreation = async (input: CreateAudiostreamInput) => {
    const newItemWeight = audiostreamData?.stationAudiostreams?.length || 0

    await toast.promise(
      createAudiostream({
        variables: {
          input: {
            ...input,
            weight: newItemWeight,
          },
        },
      }),
      {
        loading: 'Creating audio stream...',
        success: 'Audio stream created successfully',
        error: (error) =>
          `Error updating audio stream${error instanceof Error && `: ${error.message}`}`,
      }
    )
  }

  const handleAudiostreamUpdate = async (
    id: string,
    input: UpdateAudiostreamInput,
    notify: boolean = true
  ) => {
    if (notify) {
      await toast.promise(updateAudiostream({ variables: { id, input } }), {
        loading: 'Updating audio stream...',
        success: 'Audio stream updated successfully',
        error: (error) =>
          `Error updating audio stream${error instanceof Error && `: ${error.message}`}`,
      })
    } else {
      await updateAudiostream({ variables: { id, input } })
    }
  }

  const handleAudiostreamDeletion = async (id: string) => {
    await toast.promise(deleteAudiostream({ variables: { id } }), {
      loading: 'Deleting audio stream...',
      success: 'Audio stream deleted successfully',
      error: () => 'Error deleting audio stream',
    })
  }

  useEffect(() => {
    if (stationData?.station && audiostreamData?.stationAudiostreams) {
      const newState: AudiostreamManagerPageState = {
        audioData: {
          channels: audiostreamData.stationAudiostreams.map((item) => ({
            id: item.id,
            logo: item.logo,
            name: item.name,
            slug: item.slug,
            sources: item.sources,
            streamScheduleId: item.streamScheduleId,
            weight: item.weight,
          })),
        },
      }

      setAudiostreamState(newState)
    }
  }, [audiostreamData?.stationAudiostreams, stationData?.station])

  useEffect(() => {
    setInvalidStation(stationData?.station === null)
  }, [stationData])

  const stateNotNull = () => {
    return audiostreamState !== null && stationData !== undefined
  }

  return (
    <div className="w-full bg-gray-100 p-4">
      <Metadata title="Audio Stream Settings" />
      {loadingStation ? (
        <Loader />
      ) : invalidStation ? (
        <ErrorMessage
          title="Station Not Found"
          subtitle={
            <p>
              Station with ID <code>{id}</code> not found.
            </p>
          }
        />
      ) : (
        <>
          <HeadingLevel level={1} className="mb-4">
            Audio Stream Settings
          </HeadingLevel>
          {stateNotNull() && id && (
            <HeadingLevel level={2}>{stationData.station?.name}</HeadingLevel>
          )}

          <div className="mt-5 space-y-6">
            {stateNotNull() && (
              <Card title="Audio streams">
                <Audiostream
                  data={audiostreamState?.audioData}
                  loading={
                    loadingCreateAudiostream ||
                    loadingUpdateAudiostream ||
                    loadingDeleteAudiostream
                  }
                  error={createError || updateError || deleteError}
                  onModalClose={handleModalClose}
                  stationId={stationId}
                  streamScheduleOptions={streamScheduleOptions}
                  createAudiostream={handleAudiostreamCreation}
                  updateAudiostream={handleAudiostreamUpdate}
                  deleteAudiostream={handleAudiostreamDeletion}
                />
              </Card>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default AudiostreamManagerPage
